// src/pages/CourseRenewal.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { 
  ArrowLeft, 
  Users, 
  User, 
  Check, 
  CreditCard,
  Tag,
  Calculator,
  Crown,
  Sparkles,
  Shield,
  ChevronDown
} from 'lucide-react';
import {
  COURSE_PACKAGES,
  getSortedPackages,
  computePrice,
  canUserRenew,
  isCourseExpired,
  convertFirestoreDate  // ✅ NEW: Import the helper function
} from '../config/coursePackages';
import { 
  createRenewalCheckoutSession, 
  renewWithSavedPayment,
  createMultiChildRenewalCheckout,
  renewMultipleChildrenWithSavedPayment,
  renewWithPerChildPackages,
  createCheckoutSession,
  validateDiscountCode,
  stripePromise 
} from '../config/stripe';
import { getChildren, isAdultAccount } from '../utils/childrenHelper';
import { resolveDiscount } from '../utils/discountPricing';
import ProfileCompletionModal from '../components/ProfileCompletionModal';
import { isProfileComplete } from '../config/locationData';
import { isAdmin } from '../config/adminConfig';

function CourseRenewal() {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // User and course data
  const [userProfile, setUserProfile] = useState(null);
  const [courseData, setCourseData] = useState(null);
  
  // ✅ PHASE 3: Multi-child support
  const [childIds, setChildIds] = useState([]); // IDs of children being renewed
  const [childrenToRenew, setChildrenToRenew] = useState([]); // Full child objects with packageId for each
  
  // Form state
  const [discountCode, setDiscountCode] = useState('');
  const [useExistingPayment, setUseExistingPayment] = useState(true);
  
  // REMOVED: selectedPackage and quantity - now each child has their own packageId
  
  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState(null);
  const [discountValidation, setDiscountValidation] = useState(null);
  const [showPackageDropdown, setShowPackageDropdown] = useState(false);
  
  // Price calculation state
  const [priceBreakdown, setPriceBreakdown] = useState(null);

  // ✅ PHASE 3: Get child IDs from navigation state
  const location = useLocation();
  
useEffect(() => {
  // Read childIds directly from location.state to avoid race condition
  // where loadUserProfile fires before setChildIds state update settles
  const incomingChildIds = location.state?.childIds || [];
  if (incomingChildIds.length > 0) {
    setChildIds(incomingChildIds);
    console.log('📊 CourseRenewal - Renewing children:', incomingChildIds);
  }
  loadUserProfile(incomingChildIds);
}, [user]);

  useEffect(() => {
    // PHASE 3: Recalculate price when children's packages or discount changes
    calculatePrice();
  }, [childrenToRenew, discountValidation]);

  const loadUserProfile = async (incomingChildIds = []) => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    try {
      setIsLoading(true);
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      
      if (userDoc.exists()) {
        const userData = userDoc.data();
        setUserProfile(userData);
        const adultMode = isAdultAccount(userData);

        // ✅ PHASE 3: Get all children and filter by childIds
        const allChildren = getChildren(userData);
        
        // ✅ CRITICAL FIX: If user has children array but childIds is empty,
        // immediately use all children IDs to prevent falling into legacy path.
        // This fixes the race condition where childIds state hasn't updated yet.
        let effectiveChildIds = incomingChildIds;
        if (effectiveChildIds.length === 0 && userData.children && Array.isArray(userData.children) && allChildren.length > 0) {
          // Only auto-select children that actually have a course. A courseless
          // child (Phase 1 self-signup trial sibling) has nothing to renew.
          effectiveChildIds = allChildren.filter(c => c.course).map(c => c.id);
          setChildIds(effectiveChildIds);
          console.log('🔧 Auto-detected children array user, setting childIds immediately:', effectiveChildIds);
        }

        if (effectiveChildIds.length > 0) {
          // Multi-child renewal: filter by effectiveChildIds.
          // Guard on child.course: a courseless child (Phase 1 self-signup trial
          // sibling) must never enter the renewal flow — the parent could
          // otherwise be charged to "renew" a course that never existed, and the
          // renewal Cloud Function would grant that child an active course.
          const selectedChildren = allChildren.filter(child =>
            effectiveChildIds.includes(child.id) && child.course
          );
          setChildrenToRenew(selectedChildren);
          
          
          // PHASE 3: Set each child's packageId to their current/last plan as default
          const childrenWithDefaults = selectedChildren.map(child => {
            const defaultPackage = child.course?.packageId || (adultMode ? 'course_6week_adult_group' : 'course_6week_group');
            console.log(`Setting default package for ${child.name}: ${defaultPackage}`);
            return {
              ...child,
              packageId: defaultPackage
            };
          });
          
          setChildrenToRenew(childrenWithDefaults);
          
          console.log('Children with default packages:', childrenWithDefaults.map(c => ({
            name: c.name,
            currentPackage: c.course?.packageId,
            selectedPackage: c.packageId
          })));
          
          console.log('Children with default packages:', childrenWithDefaults.map(c => ({
            name: c.name,
            currentPackage: c.course?.packageId,
            selectedPackage: c.packageId
          })));

          // REMOVED: We don't need setCourseData anymore
          // Date calculations will be done per-child in the backend
          
          console.log('📊 Multi-child renewal:', {
            totalChildren: allChildren.length,
            selectedCount: selectedChildren.length,
            children: selectedChildren.map(c => c.name)
          });
        } else {
          // After migration, all users should have children array via getChildren()
          // This path is a safety net only. Still exclude courseless children
          // (Phase 1 self-signup trial siblings) — they have nothing to renew.
          const fallbackChildren = allChildren.filter(c => c.course);
          setChildrenToRenew(fallbackChildren);
          
        if (fallbackChildren.length > 0) {
            const childrenWithDefaults = fallbackChildren.map(child => ({
              ...child,
              packageId: child.course?.packageId || (adultMode ? 'course_6week_adult_group' : 'course_6week_group')
            }));
            setChildrenToRenew(childrenWithDefaults);
          }
        }
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
      setError('Failed to load your account information');
    } finally {
      setIsLoading(false);
    }
  };

  // PHASE 3: Update individual child's package selection
  const updateChildPackage = (childId, packageId) => {
    console.log(`updateChildPackage called: childId=${childId}, packageId=${packageId}`);
    
    setChildrenToRenew(prevChildren => {
      const updated = prevChildren.map(child => {
        if (child.id === childId) {
          console.log(`Updating child ${child.name} packageId from ${child.packageId} to ${packageId}`);
          return { ...child, packageId };
        }
        return child;
      });
      
      console.log('Updated childrenToRenew:', updated);
      return updated;
    });
  };

  // ✅ NEW: Helper function to format dates properly
  const formatDate = (date) => {
    const convertedDate = convertFirestoreDate(date);
    if (!convertedDate) return 'N/A';
    
    return convertedDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // ✅ NEW: Helper function to calculate renewal dates
  // PHASE 3: Updated to use payment date + 7 days
  const calculateRenewalDates = () => {
    // New logic: Course starts 7 days after payment, regardless of current status
    const paymentDate = new Date();
    const newStartDate = new Date(paymentDate);
    newStartDate.setDate(newStartDate.getDate() + 7);

    // Use longest duration among selected packages for display
    let longestDuration = 42; // Default 6 weeks
    childrenToRenew.forEach(child => {
      const pkg = COURSE_PACKAGES[child.packageId];
      if (pkg && pkg.duration.days > longestDuration) {
        longestDuration = pkg.duration.days;
      }
    });

    const newEndDate = new Date(newStartDate);
    newEndDate.setDate(newStartDate.getDate() + longestDuration);

    return {
      paymentDate,
      newStartDate,
      newEndDate
    };
  };

// PHASE 3: Calculate total price from all children's individual packages
  const calculatePrice = () => {
    if (childrenToRenew.length === 0) return;
    
    // Calculate subtotal from each child's selected package
    let subtotal = 0;
    const breakdown = [];
    
    childrenToRenew.forEach(child => {
      const pkg = COURSE_PACKAGES[child.packageId];
      if (pkg) {
        subtotal += pkg.price;
        breakdown.push({
          childName: child.name,
          packageName: pkg.name,
          price: pkg.price
        });
      }
    });
    
    // Discount priced against the REAL (possibly mixed-package) basket — see
    // src/utils/discountPricing.js. The old firstPackage×count shortcut showed a
    // figure that didn't match what Stripe / the saved-card charge actually took
    // on a mixed basket. DISPLAY ONLY: the checkout calls are untouched.
    const dRes = resolveDiscount(
      discountValidation?.valid ? discountValidation.coupon : null,
      subtotal,
    );
    const discountApplies =
      !!discountValidation?.valid && !dRes.wrongCurrency && dRes.amount > 0;
    const discountAmount = discountApplies ? dRes.amount : 0;
    const total = Math.max(0, subtotal - discountAmount);

    setPriceBreakdown({
      children: breakdown,
      subtotal: subtotal,
      discountAmount: discountAmount,
      total: total,
      discountApplies: discountApplies,
      discountWrongCurrency: dRes.wrongCurrency,
    });
  };

  const handleDiscountCodeChange = async (e) => {
    const code = e.target.value.toUpperCase();
    setDiscountCode(code);
    
    if (!code.trim()) {
      setDiscountValidation(null);
      return;
    }
    
    try {
      // Validate the code and get its coupon back only — the displayed discount
      // is priced by resolveDiscount against the real basket in calculatePrice,
      // so this call's own discountAmount/finalAmount are unused. The real
      // basket subtotal (cents) IS sent so the server can check a code's
      // minimum_amount against a mixed-package basket. Package + quantity 1 are
      // just a valid shape for the endpoint.
      const firstPackage = childrenToRenew[0]?.packageId || 'course_6week_group';
      const subtotalCents = Math.round(
        childrenToRenew.reduce((sum, c) => {
          const pkg = COURSE_PACKAGES[c.packageId];
          return sum + (pkg ? pkg.price : 0);
        }, 0) * 100,
      );
      const validation = await validateDiscountCode(code, firstPackage, 1, subtotalCents);
      setDiscountValidation(validation);
    } catch (error) {
      setDiscountValidation({
        valid: false,
        error: error.message
      });
    }
  };

  const handleRenewal = async () => {
    // DEBUG: Check what we actually have
    console.log('=== HANDLE RENEWAL DEBUG ===');
    console.log('childrenToRenew full objects:', childrenToRenew);
    console.log('PackageIds:', childrenToRenew.map(c => ({
      name: c.name,
      packageId: c.packageId,
      hasPackageId: !!c.packageId,
      fullChild: c
    })));

    // PHASE 3: Per-child renewal with unified backend approach
    if (!user || childrenToRenew.length === 0) {
      setError('Missing user or children information');
      return;
    }

    // Validate all children have packageId selected
    const hasInvalidChildren = childrenToRenew.some(child => !child.packageId);
    if (hasInvalidChildren) {
      setError('Please select a plan for each child');
      return;
    }

    setIsProcessing(true);
    setError(null);

    try {
      console.log('Starting per-child renewal:', {
        childCount: childrenToRenew.length,
        children: childrenToRenew.map(c => ({
          name: c.name,
          packageId: c.packageId
        })),
        useExistingPayment,
        hasSavedPaymentMethod: !!userProfile.payment?.savedPaymentMethodId,
        discountCode
      });

      // Prepare children data for payment (per-child format)
      const childrenData = childrenToRenew.map(child => ({
        name: child.name,
        dateOfBirth: child.dateOfBirth,
        packageId: child.packageId,
        childId: child.id
      }));

      if (useExistingPayment && userProfile.payment?.savedPaymentMethodId) {
        console.log('Using saved payment for per-child renewal...');
        
        // Call new backend function for saved payment renewal
        const result = await renewWithPerChildPackages(
          user.uid,
          childrenData,
          discountCode || null
        );
        
        if (result.success) {
          console.log('Per-child renewal with saved payment successful');
          navigate('/payment-success?renewal=true');
        } else {
          throw new Error(result.error || 'Payment failed');
        }
        
      } else {
        console.log('Creating per-child checkout session...');
        
        // VALIDATE children data before sending
        console.log('=== PRE-VALIDATION ===');
        console.log('childrenData:', JSON.stringify(childrenData, null, 2));
        
        const invalidChildren = childrenData.filter(c => !c.packageId);
        if (invalidChildren.length > 0) {
          console.error('Children with null packageId:', invalidChildren);
          throw new Error(`Missing packageId for: ${invalidChildren.map(c => c.name).join(', ')}`);
        }
        
        const stripe = await stripePromise;
        if (!stripe) {
          throw new Error('Stripe failed to load');
        }

        console.log('Calling createCheckoutSession with children:', JSON.stringify(childrenData));

        // Call unified createCheckoutSession with per-child format
        const session = await createCheckoutSession(
          user.uid,
          null, // packageId - not used in per-child format
          user.email,
          1, // quantity - not used in per-child format
          false, // savePaymentMethod
          discountCode || null,
          true, // isRenewal
          null, // currentEndDate - not needed anymore
          childrenData // PASS CHILDREN DATA
        );

        console.log('Per-child checkout session created:', session);

        const { error } = await stripe.redirectToCheckout({
          sessionId: session.sessionId,
        });

        if (error) {
          console.error('Stripe redirect error:', error);
          throw error;
        }
      }
    } catch (error) {
      console.error('Renewal error:', error);
      // A promotion-code restriction failure (below minimum, or first-order-only
      // on a renewal — the server refuses these before charging) belongs next to
      // the coupon field. The charge did NOT happen; the customer clears or
      // changes the code and retries.
      if (error && error.discountError) {
        setDiscountValidation({ valid: false, error: error.message });
        setError(null);
      } else {
        setError(`Renewal failed: ${error.message}. Please try again or contact support.`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const canUserRenewCourse = () => {
    // ✅ Multi-child support: Check if ANY selected child is eligible for renewal
    if (childrenToRenew.length > 0) {
      // Check each selected child
      for (const child of childrenToRenew) {
        if (!child.course) continue;
        
        // If at least one child is eligible for renewal, allow it
        if (canUserRenew(child.course.endDate, child.course.earlyRenewalDate)) {
          return true;
        }
      }
      return false; // No children eligible
    }
    
    // Legacy single-child check
    if (!courseData) return false;
    return canUserRenew(courseData.endDate, courseData.earlyRenewalDate);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <div className="text-purple-600 text-xl font-bold">Loading renewal options...</div>
        </div>
      </div>
    );
  }

  // Nothing renewable — e.g. a family whose only child is a Phase 1 self-signup
  // trial (course:null, filtered out above). Show a clear message instead of the
  // half-empty renewal form (no plan card, no total, disabled button). Gated on
  // !error so a genuine load failure still surfaces its message below.
  if (!error && childrenToRenew.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 flex items-center justify-center p-4">
        <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl text-center max-w-md w-full">
          <Crown size={40} className="text-purple-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Nothing to renew</h1>
          <p className="text-gray-600 mb-6">
            You don't have any courses to renew right now.
          </p>
          <button
            onClick={() => navigate('/account')}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-6 rounded-xl transition-all duration-200"
          >
            Back to Account
          </button>
        </div>
      </div>
    );
  }

  // PHASE 3: Removed early renewal window restriction
  // Users can now renew anytime - courses will start 7 days after payment

  const renewalDates = calculateRenewalDates();
  // Removed selectedPkg - no longer needed with per-child selection
  const availableRenewalPackages = getSortedPackages().filter(pkg =>
    isAdultAccount(userProfile) ? pkg.audience === 'adult' : pkg.audience === 'child'
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 p-4">
      {/* Contact-info gate: renewal requires the contact fields first.
          Staff are exempt; already-complete profiles never see this. */}
      {userProfile && !isAdmin(user?.uid) && !isProfileComplete(userProfile) && (
        <ProfileCompletionModal
          userData={userProfile}
          onComplete={() => loadUserProfile(childIds)}
        />
      )}
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/account')}
            className="p-3 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all duration-200 backdrop-blur-sm"
          >
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-3xl font-bold text-white">Renew Your Subscription</h1>
        </div>

        {/* Error Display */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="text-red-800">{error}</div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left side - Package Selection & Payment Method */}
          <div className="space-y-6">
            {/* Package Selection */}
            {/* PHASE 3: Per-Child Plan Selection */}
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <Crown size={24} className="text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-800">
                  {isAdultAccount(userProfile) ? 'Choose Your Plan' : 'Choose Plans for Each Child'}
                </h2>
              </div>

              <div className="space-y-6">
                {childrenToRenew.map((child) => (
                  <div key={child.id} className="border-2 border-gray-200 rounded-xl p-5">
                    {/* Child Name */}
                    <div className="flex items-center gap-2 mb-4">
                      <Users size={20} className="text-purple-600" />
                      <h3 className="font-bold text-lg text-gray-800">{child.name}</h3>
                      {child.course?.packageId && (
                        <span className="ml-auto text-xs text-gray-500">
                          Current Course: {COURSE_PACKAGES[child.course.packageId]?.name}
                        </span>
                      )}
                    </div>

                    {/* Plan Selection Cards */}
                    <div className={`grid gap-4 ${availableRenewalPackages.length === 1 ? 'grid-cols-1 max-w-xs' : availableRenewalPackages.length <= 2 ? 'grid-cols-2' : 'grid-cols-2 lg:grid-cols-3'}`}>
                      {availableRenewalPackages.map(pkg => (
                        <div
                          key={pkg.id}
                          onClick={() => updateChildPackage(child.id, pkg.id)}
                          className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all ${
                            child.packageId === pkg.id
                              ? 'border-purple-600 bg-purple-50'
                              : 'border-gray-300 hover:border-purple-300'
                          }`}
                        >
                          <div className="flex items-center justify-center mb-2">
                            {pkg.type === 'group' ? (
                              <Users className={`${child.packageId === pkg.id ? 'text-purple-600' : 'text-gray-400'}`} size={32} />
                            ) : (
                              <User className={`${child.packageId === pkg.id ? 'text-purple-600' : 'text-gray-400'}`} size={32} />
                            )}
                            {child.packageId === pkg.id && (
                              <div className="absolute top-2 right-2">
                                <Check size={20} className="text-purple-600" />
                              </div>
                            )}
                          </div>
                          <div className="text-center">
                            <div className="font-bold text-gray-800 mb-1">{pkg.shortLabel}</div>
                            <div className="text-sm text-gray-600 mb-1">{pkg.subtitle}</div>
                            <div className="font-bold text-purple-600 text-lg">${pkg.price}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

            {/* PHASE 3: Removed - plan selection cards above already show all children */}

              {/* Discount Code */}
              <div className="mt-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Discount Code (Optional)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={discountCode}
                    onChange={handleDiscountCodeChange}
                    placeholder="Enter discount code"
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent pl-10"
                  />
                  <Tag size={20} className="absolute left-3 top-3.5 text-gray-400" />
                </div>
                {discountValidation && (
                  <div className={`mt-2 text-sm ${priceBreakdown?.discountApplies ? 'text-green-600' : 'text-red-600'}`}>
                    {!discountValidation.valid
                      ? `❌ ${discountValidation.error}`
                      : priceBreakdown?.discountWrongCurrency
                        ? "❌ This code can't be applied to your order."
                        : priceBreakdown?.discountApplies
                          ? `✅ Discount applied: $${(priceBreakdown.discountAmount || 0).toFixed(2)} off`
                          : '❌ This code has no effect on your order.'}
                  </div>
                )}
              </div>
            </div>

            {/* Payment Method Selection */}
            <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl">
              <div className="flex items-center gap-3 mb-6">
                <CreditCard size={24} className="text-purple-600" />
                <h2 className="text-2xl font-bold text-gray-800">Payment Method</h2>
              </div>

              <div className="space-y-3">
                {/* Show saved payment option only if user has saved payment method */}
                {userProfile?.payment?.savedPaymentMethodId && (
                  <div
                    onClick={() => setUseExistingPayment(true)}
                    className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                      useExistingPayment ? 'border-purple-500 bg-purple-50' : 'border-gray-300'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded-full border-2 ${
                      useExistingPayment ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
                    }`}>
                      {useExistingPayment && <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>}
                    </div>
                    <CreditCard size={20} className="text-purple-600" />
                    <div>
                      <div className="font-semibold text-gray-800">Use saved payment method</div>
                      <div className="text-sm text-gray-600">Quick and secure</div>
                    </div>
                  </div>
                )}
                
                <div
                  onClick={() => setUseExistingPayment(false)}
                  className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all ${
                    !useExistingPayment ? 'border-purple-500 bg-purple-50' : 'border-gray-300'
                  }`}
                >
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    !useExistingPayment ? 'border-purple-500 bg-purple-500' : 'border-gray-300'
                  }`}>
                    {!useExistingPayment && <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>}
                  </div>
                  <CreditCard size={20} className="text-gray-600" />
                  <div>
                    <div className="font-semibold text-gray-800">Use different payment method</div>
                    <div className="text-sm text-gray-600">Enter new card details</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right side - Price Summary & Checkout */}
          <div className="bg-white/95 backdrop-blur-sm rounded-3xl p-8 shadow-2xl">
            <div className="text-center mb-6">
              <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-bold mb-4">
                <Calculator size={16} />
                Price Summary
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">Course Renewal</h2>
              <p className="text-gray-600">Your new course will start after your current one ends</p>
            </div>

            {/* PHASE 3: Updated Course Timeline */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
              <h3 className="font-semibold text-blue-800 mb-2">Course Schedule</h3>
              <div className="text-blue-700 text-sm space-y-1">
                <div>Payment today: <strong>{formatDate(renewalDates.paymentDate)}</strong></div>
                <div>Courses start: <strong>{formatDate(renewalDates.newStartDate)}</strong></div>
                <div className="text-xs text-gray-600 mt-2">
                  New courses start 7 days after payment
                </div>
              </div>
            </div>

            {/* PHASE 3: Per-Child Price Breakdown */}
            {priceBreakdown && (
              <div className="space-y-3 mb-6">
                {/* Per-child breakdown */}
                {priceBreakdown.children.map((child, index) => (
                  <div key={index} className="flex justify-between items-center py-2 border-b border-gray-100">
                    <div>
                      <div className="font-medium text-gray-800">{child.childName}</div>
                      <div className="text-xs text-gray-500">{child.packageName}</div>
                    </div>
                    <span className="font-semibold text-gray-800">${child.price}</span>
                  </div>
                ))}
                
                {/* Subtotal */}
                <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">Subtotal</span>
                  <span className="font-semibold">${priceBreakdown.subtotal}</span>
                </div>
                
                {/* Discount */}
                {priceBreakdown.discountAmount > 0 && (
                  <div className="flex justify-between items-center py-2 text-green-600">
                    <span>Discount</span>
                    <span className="font-semibold">-${priceBreakdown.discountAmount}</span>
                  </div>
                )}
                
                {/* Total */}
                <div className="border-t-2 border-gray-300 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-bold text-gray-800">Total</span>
                    <span className="text-2xl font-bold text-purple-600">${priceBreakdown.total}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Renewal Button */}
            <button
              onClick={handleRenewal}
              disabled={isProcessing || !priceBreakdown}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50"
            >
              {isProcessing ? (
                <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <>
                  <Sparkles size={20} />
                  {useExistingPayment && userProfile?.payment?.savedPaymentMethodId ? 
                    'Renew Now' : 
                    'Continue to Payment'
                  }
                </>
              )}
            </button>

            {/* Security Badge */}
            <div className="flex items-center justify-center gap-2 mt-4 text-sm text-gray-600">
              <Shield size={16} />
              <span>Secure payment powered by Stripe</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CourseRenewal;